import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import { intervalToDuration } from 'date-fns'
import useDeleteCleaningZone from 'hooks/mission/useDeleteCleaningZone'
import useZoomToPolygon from 'hooks/useZoomToPolygon'
import { ReactComponent as ZoneSvg } from 'images/icons/cleaning_zone_small.svg'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { ReactComponent as SortSvg } from 'images/icons/sort_icon.svg'
import { ReactComponent as ZoneRouteSvg } from 'images/icons/zone_route.svg'
import Feature from 'ol/Feature'
import { Polygon } from 'ol/geom'
import { DragEvent, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useMissionStore from 'store/mission'
import { MISSION_CALCULATING_STATUSES } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { ICleaningZone } from 'utils/interfaces'
import { useShallow } from 'zustand/react/shallow'

import style from './ZoneItem.module.css'

const CleaningZoneItem = ({
  zone,
  number,
  hideActions,
  isDragging = false,
  isDragOver = false,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: {
  zone: ICleaningZone
  number: number
  hideActions?: boolean
  isDragging?: boolean
  isDragOver?: boolean
  onDragStart?: (zoneId: number) => void
  onDragOver?: (zoneId: number) => void
  onDragLeave?: (zoneId: number) => void
  onDrop?: (zoneId: number) => void
  onDragEnd?: () => void
}) => {
  const { t } = useTranslation()
  const { zoomToPolygon } = useZoomToPolygon()

  const { highlightedFeatureInList, deleteCleaningZone, setChangeZone } = useMissionStore(
    useShallow(state => ({
      highlightedFeatureInList: state.highlightedFeatureInList,
      deleteCleaningZone: state.deleteCleaningZone,
      setChangeZone: state.setChangeZone,
    }))
  )

  const { mutateAsync: setDeleteCleaningZone } = useDeleteCleaningZone()

  const highlighted = useMemo(() => {
    return highlightedFeatureInList === zone.id
  }, [highlightedFeatureInList, zone.id])

  const onZoneClicked = () => {
    const feature = new Feature({
      // @ts-ignore
      geometry: new Polygon(zone.geometry.coordinates!).transform('EPSG:4326', 'EPSG:3857'),
    })

    zoomToPolygon(feature.getGeometry() as Polygon)
  }

  const zoneId = zone.properties?.zone_id
  const rowRef = useRef<HTMLDivElement>(null)

  const onDragStartHandler = (e: DragEvent<HTMLSpanElement>) => {
    if (zoneId === undefined) return
    e.dataTransfer.setData('text/plain', String(zoneId))
    e.dataTransfer.effectAllowed = 'move'
    if (rowRef.current) {
      e.dataTransfer.setDragImage(rowRef.current, 20, 20)
    }
    onDragStart?.(zoneId)
  }

  const onDragOverHandler = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (zoneId !== undefined) {
      onDragOver?.(zoneId)
    }
  }

  const onDragLeaveHandler = (e: DragEvent<HTMLDivElement>) => {
    if (zoneId !== undefined && !e.currentTarget.contains(e.relatedTarget as Node)) {
      onDragLeave?.(zoneId)
    }
  }

  const onDropHandler = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (zoneId !== undefined) {
      onDrop?.(zoneId)
    }
  }

  const onEditZoneClicked = () => {
    setChangeZone(zone)
  }

  const onRemoveZoneClicked = () => {
    if (zone.properties?.zone_id && zone.properties?.zone_type) {
      setDeleteCleaningZone(zone.properties?.zone_id as number)
        .then(() => {
          deleteCleaningZone(zone)
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log('Error deleting zone', error)
        })
    }
  }

  const formatDuration = (seconds: number) => {
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 })
    return duration.days
      ? `${duration.days}d:${duration.hours}h:${duration.minutes}m`
      : `${duration.hours}h:${duration.minutes}m`
  }

  return (
    <div
      className={clsx(
        style.zoneItem,
        highlighted && style.highlighted,
        isDragging && style.dragging,
        isDragOver && style.dragOver
      )}
      onClick={onZoneClicked}
      ref={rowRef}
      onDragOver={onDragOverHandler}
      onDragLeave={onDragLeaveHandler}
      onDrop={onDropHandler}
    >
      <div className={style.zoneItemColSmall}>
        <span
          className={style.dragHandle}
          draggable
          onDragStart={onDragStartHandler}
          onDragEnd={onDragEnd}
          onClick={e => e.stopPropagation()}
        >
          <SortSvg className={style.dragHandleIcon} />
        </span>
        #{number}
      </div>
      <div className={style.zoneItemColSmall}>
        <ZoneSvg className={style.icon} />
      </div>
      <div className={style.zoneItemCol}>{zone.properties?.name}</div>
      <div className={style.zoneItemCol}>
        <ZoneRouteSvg />
        <div className={style.marginLeft}>
          {zone.properties?.status === MISSION_CALCULATING_STATUSES.OK && (
            <>
              <div>{t('mission.cleaning_route')}</div>
              <div className={style.zoneAdditionalInfo}>
                {zone?.properties?.area && (
                  <>
                    <span>{t('mission.area')}</span>
                    <span className={style.bold}>
                      {Math.round(zone?.properties?.area || 0)}m<sup>2</sup>
                    </span>
                  </>
                )}
                {zone.properties?.length && (
                  <span className={style.marginLeftSmall}>
                    <span>{t('mission.length')}</span>
                    <span className={style.bold}>{Math.round(zone.properties?.length)}m</span>
                  </span>
                )}
                {zone.properties?.executionTime && (
                  <span className={style.marginLeftSmall}>
                    <span>{t('mission.execution_time')}</span>
                    <span className={style.bold}>{formatDuration(zone.properties?.executionTime)}</span>
                  </span>
                )}
              </div>
            </>
          )}
          {zone.properties?.status === MISSION_CALCULATING_STATUSES.CALCULATING &&
            t('zone_details.cleaning_zone_calculating')}
          {zone.properties?.status === MISSION_CALCULATING_STATUSES.NEW && t('mission_status.new')}
          {zone.properties?.status === MISSION_CALCULATING_STATUSES.ERROR && t('mission_status.error')}
          {zone.properties?.status === MISSION_CALCULATING_STATUSES.PARTIAL_CALCULATED &&
            t('mission_status.partial_calculated')}
        </div>
      </div>
      <div className={style.zoneItemColSmall}>
        {!hideActions && (
          <ActionsMenu>
            <ActionsMenuItem onClick={onEditZoneClicked} data-test-id={getTestId('edit-zone-list')}>
              <span className={style.menuItemIcon} />
              <span data-test-id={getTestId('edit-title')}>{t('common.edit')}</span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={onRemoveZoneClicked} data-test-id={getTestId('remove-zone-list')}>
              <RemoveSvg className={style.menuItemIcon} />
              <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
            </ActionsMenuItem>
          </ActionsMenu>
        )}
      </div>
    </div>
  )
}

export default CleaningZoneItem
